import './App.css';
import React from 'react';
import {BrowserRouter, Route, Routes} from 'react-router-dom';
import {createRoot} from 'react-dom/client';
import {AppearanceListener} from '@common/admin/appearance/commands/appearance-listener';
import {LandingPage} from './landing/landing-page';
import {CommonProvider} from '@common/core/common-provider';
import {ActiveWorkspaceProvider} from '@common/workspace/active-workspace-id-context';
import {GuestRoute} from '@common/auth/guards/guest-route';
import {AuthRoutes} from '@common/auth/auth-routes';
import {NotFoundPage} from '@common/ui/not-found-page/not-found-page';
import {AuthRoute} from '@common/auth/guards/auth-route';
import {FullPageLoader} from '@common/ui/progress/full-page-loader';
import {BillingRoutes} from '@common/billing/billing-routes';
import {NotificationRoutes} from '@common/notifications/notification-routes';
import {DynamicHomepage} from '@common/ui/dynamic-homepage';
import {CookieNotice} from '@common/ui/cookie-notice/cookie-notice';
import {ContactUsPage} from '@common/contact/contact-us-page';
import {CustomPageLayout} from '@common/custom-page/custom-page-layout';
import {ToastContainer} from '@common/ui/toast/toast-container';
import {LandingPageContent} from './landing/landing-page-content';
import {useAuth} from '@common/auth/use-auth';
import {EmailVerificationPage} from '@common/auth/ui/email-verification-page/email-verification-page';
import * as Sentry from '@sentry/react';
import {BrowserTracing} from '@sentry/tracing';
import {rootEl} from '@common/core/root-el';
import {useSettings} from '@common/core/settings/use-settings';
import {getBootstrapData} from '@common/core/bootstrap-data/use-backend-bootstrap-data';
import {ignoredSentryErrors} from '@common/errors/ignored-sentry-errors';
import {DialogStoreOutlet} from '@common/ui/overlays/store/dialog-store-outlet';
import WXSDK from '@common/wx/lib';
import {useEffect} from 'react';
import {
  ImproveAccount,
  ImproveWrapper,
} from '@common/auth/ui/ImproveAccountInfo';

(
  window as any
).global_logo = `data:image/png;base64,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`;

(
  window as any
).global_blue_logo = `data:image/png;base64,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`;

const AdminRoutes = React.lazy(() => import('@common/admin/admin-routes'));
const DriveRoutes = React.lazy(() => import('./drive/drive-routes'));
const SwaggerApiDocs = React.lazy(
  () => import('@common/swagger/swagger-api-docs-page')
);

declare module '@common/core/settings/settings' {
  interface Settings {
    homepage: {
      appearance: LandingPageContent;
      type: 'loginPage' | 'registerPage' | string;
      value?: any;
    };
    drive: {
      details_default_visibility: boolean;
      default_view: 'list' | 'grid';
      send_share_notification: boolean;
    };
    share: {
      suggest_emails: boolean;
    };
    ads?: {
      drive?: string;
      'file-preview'?: string;
      'landing-top'?: string;
      disable?: boolean;
    };
  }
}

const sentryDsn = getBootstrapData().settings.logging.sentry_public;
if (sentryDsn && import.meta.env.PROD) {
  Sentry.init({
    dsn: sentryDsn,
    integrations: [new BrowserTracing()],
    tracesSampleRate: 1.0,
    ignoreErrors: ignoredSentryErrors,
  });
}

const root = createRoot(rootEl);
root.render(
  <CommonProvider>
    <Router />
  </CommonProvider>
);

function Router() {
  const {
    billing,
    notifications,
    require_email_confirmation,
    api,
    html_base_uri,
  } = useSettings();

  WXSDK.instance.init();

  const {user, hasPermission} = useAuth();

  if (user != null && require_email_confirmation && !user.email_verified_at) {
    return (
      <BrowserRouter>
        <ToastContainer />
        <Routes>
          <Route path="*" element={<EmailVerificationPage />} />
        </Routes>
        <DialogStoreOutlet />
      </BrowserRouter>
    );
  }

  useEffect(() => {
    if (location.href.includes('drive/s')) return;
    WXSDK.instance.callHandle(WXSDK.instance.sharedPage, [
      {
        title: '订单平台',
        desc: '为你提供"样稿"文件分享安全保护',
        imgUrl: '',
        link: location.href,
      },
    ]);
  }, []);

  return (
    <BrowserRouter basename={html_base_uri}>
      <AppearanceListener />
      <CookieNotice />
      <ToastContainer />
      <Routes>
        <Route
          path="/"
          element={
            <DynamicHomepage
              homepageResolver={() => (
                <GuestRoute>
                  <LandingPage />
                </GuestRoute>
              )}
            />
          }
        />

        <Route
          path="/improve"
          element={
            <React.Suspense fallback={<FullPageLoader />}>
              <ImproveAccount />
            </React.Suspense>
          }
        />

        <Route
          path="/drive/*"
          element={
            <React.Suspense fallback={<FullPageLoader />}>
              <ActiveWorkspaceProvider>
                <ImproveWrapper>
                  <DriveRoutes />
                </ImproveWrapper>
              </ActiveWorkspaceProvider>
            </React.Suspense>
          }
        />
        <Route
          path="/admin/*"
          element={
            <AuthRoute permission="admin.access">
              <React.Suspense fallback={<FullPageLoader />}>
                <AdminRoutes />
              </React.Suspense>
            </AuthRoute>
          }
        />
        {AuthRoutes}
        {billing.enable && BillingRoutes}
        {notifications.integrated && NotificationRoutes}
        {api?.integrated && hasPermission('api.access') && (
          <Route
            path="api-docs"
            element={
              <React.Suspense fallback={<FullPageLoader />}>
                <SwaggerApiDocs />
              </React.Suspense>
            }
          ></Route>
        )}
        <Route path="contact" element={<ContactUsPage />}></Route>
        <Route path="pages/:pageSlug" element={<CustomPageLayout />}></Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <DialogStoreOutlet />
    </BrowserRouter>
  );
}
